/**
 * DOM application of the resolved theme.
 *
 * The inline pre-bootstrap script in ``ui/index.html`` sets the
 * ``data-theme`` attribute on ``<html>`` before React mounts to
 * avoid a flash-of-default-theme per REQ-UI-044. After React
 * mounts, :func:`useApplyTheme` keeps that attribute in sync with
 * the user's current choice — including the reactive flips that
 * REQ-UI-041 requires for ``"system"`` mode.
 *
 * Cross-references:
 *   - REQ-UI-040 (three modes).
 *   - REQ-UI-041 (system-mode reactivity).
 *   - REQ-UI-044 (pre-bootstrap script — this hook is the
 *     post-mount reconciler).
 */
import { useEffect } from "react";
import { useResolvedTheme } from "@/theme/resolve-theme";
import type { Theme } from "@/theme/tokens";

/**
 * Apply the user's theme choice to ``<html data-theme="…">`` and
 * keep it in sync with ``prefers-color-scheme`` for ``"system"``.
 *
 * The hook returns the resolved theme so the caller can use it
 * for ancillary state (logging, map-tile selection).
 */
export function useApplyTheme(choice: Theme): "light" | "dark" {
  const resolved = useResolvedTheme(choice);
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.setAttribute("data-theme", resolved);
  }, [resolved]);
  return resolved;
}
